import type { Permission } from '@alola/contracts';
import {
  BadgeCheck,
  Lock,
  Banknote,
  ChartColumn,
  FileText,
  Building2,
  Calculator,
  CalendarCheck,
  CalendarClock,
  Briefcase,
  ClipboardList,
  FileSignature,
  FileUp,
  HandCoins,
  History,
  House,
  Inbox,
  KeyRound,
  Landmark,
  RefreshCw,
  Wallet,
  ArrowRight,
  Building,
  LayoutDashboard,
  ListChecks,
  Megaphone,
  Network,
  Printer,
  Receipt,
  BellRing,
  CalendarRange,
  ListTree,
  NotebookPen,
  Scale,
  ShieldCheck,
  Workflow,
  ScrollText,
  SlidersHorizontal,
  UserPlus,
  Target,
  TrendingUp,
  Users,
  Warehouse,
  Construction,
  Package,
  IdCard,
  ReceiptText,
  Recycle,
  type LucideIcon,
  Trash2,
} from '@alola/ui/icons';

/**
 * The navigation model, in one place.
 *
 * Each entry names the permissions that make it worth showing. **That is presentation, not
 * security**: the server enforces every one of them again inside the query (ADR-0006), and the E2E
 * suite navigates directly to a hidden route and asserts the API still refuses. Hiding a link a
 * person cannot use is a courtesy; treating the hidden link as the control would be the classic
 * client-side authorization mistake.
 *
 * Labels are translation keys, never text, so a navigation item cannot ship in one language. Every
 * entry is a route that exists — no group is padded with a placeholder screen.
 */
export interface NavItem {
  path: string;
  labelKey: string;
  icon: LucideIcon;
  /** Shown when the actor holds **any** of these. An empty list means always shown. */
  permissions: Permission[];
  /** A live count shown beside the entry; the shell knows how to fetch each kind. */
  badge?: 'approvalsAwaiting';
}

export interface NavGroup {
  id: string;
  labelKey: string;
  items: NavItem[];
}

export const NAVIGATION: NavGroup[] = [
  {
    // Personal tools: everyone's own dashboard, tasks, payslips and requests (2026-10-26).
    id: 'overview',
    labelKey: 'nav.groupOverview',
    items: [
      { path: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard, permissions: [] },
      // Everyone has their own tasks; no permission is needed to see them (CORE-TASK-001).
      { path: '/tasks', labelKey: 'nav.tasks', icon: ListChecks, permissions: [] },
      // Everyone sees their own payslips, if they are an employee (HR-PAY-003).
      { path: '/my-payslips', labelKey: 'nav.myPayslips', icon: ReceiptText, permissions: [] },
      { path: '/my-requests', labelKey: 'nav.myRequests', icon: Inbox, permissions: [] },
      // Only for people who may decide: reading requests alone is not a reason to see an inbox.
      {
        path: '/approvals',
        labelKey: 'nav.approvals',
        icon: Inbox,
        permissions: ['approval.request.approve', 'approval.request.reject'],
        badge: 'approvalsAwaiting',
      },
    ],
  },
  {
    // Sales and customers: follow-up, the team's distribution and recycling, and the deal records.
    id: 'sales',
    labelKey: 'nav.groupSales',
    items: [
      { path: '/leads', labelKey: 'nav.crm', icon: UserPlus, permissions: ['crm.lead.view'] },
      {
        path: '/sales/recycle',
        labelKey: 'nav.recycle',
        icon: Recycle,
        permissions: ['crm.lead.assign'],
      },
      {
        path: '/sales/follow-ups',
        labelKey: 'nav.followUps',
        icon: CalendarClock,
        permissions: ['crm.team.view'],
      },
      {
        path: '/sales/team',
        labelKey: 'nav.salesTeam',
        icon: Users,
        permissions: ['crm.team.view'],
      },
      {
        path: '/customers',
        labelKey: 'nav.customers',
        icon: Users,
        permissions: ['crm.customer.view'],
      },
      {
        path: '/quotations',
        labelKey: 'nav.quotations',
        icon: Calculator,
        permissions: ['sales.quotation.view'],
      },
      {
        path: '/reservations',
        labelKey: 'nav.reservations',
        icon: CalendarCheck,
        permissions: ['sales.reservation.view'],
      },
      {
        path: '/contracts',
        labelKey: 'nav.contracts',
        icon: FileSignature,
        permissions: ['sales.contract.view'],
      },
      {
        path: '/sales/deals',
        labelKey: 'nav.salesDeals',
        icon: Briefcase,
        permissions: ['crm.team.view'],
      },
      {
        path: '/commissions',
        labelKey: 'nav.commissions',
        icon: HandCoins,
        permissions: ['sales.commission.view'],
      },
      {
        // The sales report reads pipeline, reservations and contracts: sales work, not lead entry.
        path: '/reports/sales',
        labelKey: 'nav.salesReport',
        icon: ChartColumn,
        permissions: ['crm.opportunity.view'],
      },
      {
        path: '/sales/performance',
        labelKey: 'nav.salesPerformance',
        icon: TrendingUp,
        permissions: ['crm.team.view'],
      },
      {
        path: '/settings/crm',
        labelKey: 'nav.crmSettings',
        icon: SlidersHorizontal,
        permissions: ['crm.settings.manage'],
      },
      {
        path: '/settings/lead-distribution',
        labelKey: 'nav.leadDistribution',
        icon: Workflow,
        permissions: ['crm.distribution.manage'],
      },
    ],
  },
  {
    // The catalogue. A project lookup used by another workspace's form is not a reason to show it.
    id: 'inventory',
    labelKey: 'nav.groupInventory',
    items: [
      {
        path: '/projects',
        labelKey: 'nav.projects',
        icon: Building2,
        permissions: ['inventory.unit.view'],
      },
      { path: '/units', labelKey: 'nav.units', icon: House, permissions: ['inventory.unit.view'] },
      {
        path: '/holds',
        labelKey: 'nav.holds',
        icon: Lock,
        permissions: ['inventory.hold.create', 'inventory.hold.manage'],
      },
      {
        path: '/plan-templates',
        labelKey: 'nav.planTemplates',
        icon: CalendarRange,
        permissions: ['inventory.plan.manage', 'sales.quotation.view'],
      },
    ],
  },
  {
    id: 'collections',
    labelKey: 'nav.groupCollections',
    items: [
      {
        path: '/installments',
        labelKey: 'nav.installments',
        icon: CalendarClock,
        permissions: ['collection.installment.view'],
      },
      {
        path: '/receipts',
        labelKey: 'nav.receipts',
        icon: Receipt,
        permissions: ['collection.receipt.view'],
      },
      {
        path: '/instruments',
        labelKey: 'nav.instruments',
        icon: ScrollText,
        permissions: ['collection.instrument.view'],
      },
      {
        path: '/reminders',
        labelKey: 'nav.reminders',
        icon: BellRing,
        permissions: ['collection.reminder.view'],
      },
      {
        path: '/collections/receivables',
        labelKey: 'nav.receivables',
        icon: ChartColumn,
        permissions: ['collection.report.view'],
      },
      {
        path: '/collections/refunds',
        labelKey: 'nav.refunds',
        icon: Banknote,
        permissions: ['collection.refund.view'],
      },
    ],
  },
  {
    // Treasury and banks (BMP-2 package 3): money held and moved, recorded — never executed. A
    // department that only requests payments sees its requests, not the books (2026-10-26).
    id: 'treasury',
    labelKey: 'nav.groupTreasury',
    items: [
      {
        path: '/treasury',
        labelKey: 'nav.treasuryPosition',
        icon: Landmark,
        permissions: ['treasury.report.view'],
      },
      {
        path: '/treasury/accounts',
        labelKey: 'nav.treasuryAccounts',
        icon: Wallet,
        permissions: [
          'treasury.report.view',
          'treasury.movement.record',
          'treasury.account.manage',
        ],
      },
      {
        path: '/treasury/transfers',
        labelKey: 'nav.treasuryTransfers',
        icon: ArrowRight,
        permissions: ['treasury.report.view', 'treasury.transfer.create'],
      },
      {
        path: '/treasury/payments',
        labelKey: 'nav.treasuryPayments',
        icon: HandCoins,
        permissions: ['treasury.view'],
      },
      {
        path: '/treasury/reconciliation',
        labelKey: 'nav.reconciliation',
        icon: RefreshCw,
        permissions: [
          'treasury.report.view',
          'treasury.reconciliation.manage',
          'treasury.reconciliation.approve',
        ],
      },
      {
        path: '/treasury/banks',
        labelKey: 'nav.banks',
        icon: Building,
        permissions: ['treasury.report.view', 'treasury.bank.manage'],
      },
      {
        path: '/treasury/cheque-layouts',
        labelKey: 'nav.chequeLayouts',
        icon: Printer,
        permissions: ['treasury.chequeLayout.manage', 'treasury.cheque.print'],
      },
    ],
  },
  {
    // Accounting (BMP-2, ADR-0034): the ledger, expenses, assets and their configuration.
    id: 'accounting',
    labelKey: 'nav.groupAccounting',
    items: [
      {
        path: '/finance/entries',
        labelKey: 'nav.journalEntries',
        icon: NotebookPen,
        permissions: ['finance.journal.view'],
      },
      {
        path: '/finance/invoices',
        labelKey: 'nav.invoices',
        icon: FileText,
        permissions: ['finance.invoice.view'],
      },
      {
        // Company money in one place: financial readers only, never a sales or procurement reader.
        path: '/finance/dashboard',
        labelKey: 'nav.financeDashboard',
        icon: Scale,
        permissions: ['finance.report.view'],
      },
      {
        path: '/finance/statements',
        labelKey: 'nav.financialStatements',
        icon: ChartColumn,
        permissions: ['finance.report.view'],
      },
      {
        path: '/finance/reports',
        labelKey: 'nav.financeReports',
        icon: Scale,
        permissions: ['finance.report.view'],
      },
      {
        path: '/finance/revenue-recognition',
        labelKey: 'nav.revenueRecognition',
        icon: TrendingUp,
        permissions: ['finance.report.view'],
      },
      {
        path: '/finance/accounts',
        labelKey: 'nav.chartOfAccounts',
        icon: ListTree,
        permissions: ['finance.account.view'],
      },
      {
        path: '/finance/periods',
        labelKey: 'nav.fiscalPeriods',
        icon: CalendarRange,
        permissions: ['finance.account.view'],
      },
      {
        path: '/finance/budgets',
        labelKey: 'nav.budgets',
        icon: Target,
        permissions: ['finance.budget.manage', 'finance.report.view'],
      },
      {
        path: '/expenses',
        labelKey: 'nav.expenses',
        icon: Receipt,
        permissions: ['finance.expense.view'],
      },
      {
        path: '/expenses/recurring',
        labelKey: 'nav.recurringExpenses',
        icon: RefreshCw,
        permissions: ['finance.expense.view'],
      },
      {
        path: '/finance/cash-advances',
        labelKey: 'nav.cashAdvances',
        icon: HandCoins,
        permissions: ['finance.advance.view'],
      },
      {
        path: '/finance/assets',
        labelKey: 'nav.assets',
        icon: Package,
        permissions: ['finance.asset.view'],
      },
      {
        path: '/finance/posting',
        labelKey: 'nav.postingRules',
        icon: Workflow,
        permissions: ['finance.journal.view'],
      },
      {
        path: '/finance/opening-balances',
        labelKey: 'nav.openingBalances',
        icon: Scale,
        permissions: ['finance.journal.view'],
      },
      {
        path: '/finance/onboarding',
        labelKey: 'nav.onboarding',
        icon: CalendarClock,
        permissions: ['settings.view'],
      },
    ],
  },
  {
    id: 'procurement',
    labelKey: 'nav.groupProcurement',
    items: [
      {
        path: '/procurement/requests',
        labelKey: 'nav.purchaseRequests',
        icon: ClipboardList,
        permissions: ['procurement.request.view'],
      },
      {
        path: '/procurement/orders',
        labelKey: 'nav.purchaseOrders',
        icon: FileSignature,
        permissions: ['procurement.order.view'],
      },
      {
        path: '/procurement/invoices',
        labelKey: 'nav.vendorInvoices',
        icon: Receipt,
        permissions: ['procurement.invoice.view'],
      },
      {
        path: '/procurement/payables',
        labelKey: 'nav.payables',
        icon: Scale,
        permissions: ['procurement.report.view'],
      },
      {
        path: '/procurement/vendors',
        labelKey: 'nav.vendors',
        icon: Briefcase,
        permissions: ['procurement.vendor.view'],
      },
    ],
  },
  {
    id: 'stores',
    labelKey: 'nav.groupStores',
    items: [
      { path: '/stores', labelKey: 'nav.stores', icon: Warehouse, permissions: ['stores.view'] },
    ],
  },
  {
    id: 'construction',
    labelKey: 'nav.groupConstruction',
    items: [
      {
        path: '/construction',
        labelKey: 'nav.construction',
        icon: Construction,
        permissions: ['construction.view'],
      },
    ],
  },
  {
    id: 'handover',
    labelKey: 'nav.groupHandover',
    items: [
      {
        path: '/handover',
        labelKey: 'nav.handover',
        icon: KeyRound,
        permissions: ['handover.view', 'aftersales.view'],
      },
    ],
  },
  {
    id: 'hr',
    labelKey: 'nav.groupHr',
    items: [
      { path: '/hr', labelKey: 'nav.hr', icon: IdCard, permissions: ['hr.employee.view'] },
      {
        path: '/payroll',
        labelKey: 'nav.payroll',
        icon: Banknote,
        permissions: ['hr.payroll.view'],
      },
    ],
  },
  {
    id: 'marketing',
    labelKey: 'nav.groupMarketing',
    items: [
      {
        path: '/campaigns',
        labelKey: 'nav.campaigns',
        icon: Megaphone,
        permissions: ['marketing.campaign.view'],
      },
      {
        path: '/reports/marketing',
        labelKey: 'nav.marketingReport',
        icon: ChartColumn,
        permissions: ['marketing.campaign.view'],
      },
    ],
  },
  {
    id: 'admin',
    labelKey: 'nav.groupAdmin',
    items: [
      {
        path: '/organization',
        labelKey: 'nav.organization',
        icon: Network,
        permissions: ['org.view'],
      },
      {
        path: '/admin/users',
        labelKey: 'nav.users',
        icon: KeyRound,
        permissions: ['security.account.view'],
      },
      {
        path: '/admin/audit',
        labelKey: 'nav.audit',
        icon: History,
        permissions: ['audit.view'],
      },
      {
        path: '/imports',
        labelKey: 'nav.imports',
        icon: FileUp,
        permissions: [
          'referenceData.manage',
          'crm.customer.import',
          'inventory.unit.import',
          'sales.contract.import',
          'collection.receipt.import',
        ],
      },
    ],
  },
  {
    id: 'settings',
    labelKey: 'nav.groupSettings',
    items: [
      {
        path: '/settings/company',
        labelKey: 'nav.companyIdentity',
        icon: BadgeCheck,
        permissions: ['company.profile.view'],
      },
      {
        path: '/settings/sales',
        labelKey: 'nav.salesSettings',
        icon: SlidersHorizontal,
        permissions: ['settings.view', 'numbering.view'],
      },
      {
        path: '/settings/reference-data',
        labelKey: 'nav.referenceData',
        icon: ListTree,
        permissions: ['referenceData.manage'],
      },
      {
        path: '/settings/commission-rules',
        labelKey: 'nav.commissionRules',
        icon: Scale,
        permissions: ['sales.commission.manage'],
      },
      {
        path: '/settings/approval-policies',
        labelKey: 'nav.approvalPolicies',
        icon: ShieldCheck,
        permissions: ['approval.policy.view'],
      },
      {
        path: '/settings/templates',
        labelKey: 'nav.documentTemplates',
        icon: FileText,
        permissions: ['template.view'],
      },
      {
        path: '/settings/data-reset',
        labelKey: 'nav.dataReset',
        icon: Trash2,
        // settings.manage reads the explanation only; requesting and executing keep their permissions.
        permissions: ['data.reset.request', 'data.reset.execute', 'settings.manage'],
      },
      {
        path: '/settings/readiness',
        labelKey: 'nav.readiness',
        icon: ListChecks,
        permissions: ['operations.diagnostics'],
      },
    ],
  },
];

/**
 * Record pages opened from another workspace by reference — an invoice's contract, a task's lead.
 * Guarded by the record's read permission rather than by membership of the workspace whose list it
 * belongs to, so a reference stays usable without putting the whole section in someone's menu.
 * Checked before the navigation entries; the most specific pattern first.
 */
const RECORD_ROUTES: { pattern: RegExp; permissions: Permission[] }[] = [
  {
    pattern: /^\/contracts\/[^/]+\/collections$/,
    permissions: ['collection.installment.view', 'collection.report.view'],
  },
  { pattern: /^\/contracts\/[^/]+$/, permissions: ['sales.contract.view'] },
  { pattern: /^\/customers\/[^/]+$/, permissions: ['crm.customer.view'] },
  { pattern: /^\/leads\/[^/]+$/, permissions: ['crm.lead.view'] },
  { pattern: /^\/reservations\/new$/, permissions: ['sales.reservation.create'] },
  { pattern: /^\/reservations\/[^/]+$/, permissions: ['sales.reservation.view'] },
  { pattern: /^\/quotations\/[^/]+$/, permissions: ['sales.quotation.view'] },
  { pattern: /^\/projects\/[^/]+$/, permissions: ['inventory.project.view'] },
  { pattern: /^\/units\/[^/]+$/, permissions: ['inventory.unit.view'] },
  { pattern: /^\/receipts\/[^/]+$/, permissions: ['collection.receipt.view'] },
  { pattern: /^\/finance\/budgets\/[^/]+$/, permissions: ['finance.budget.view'] },
  { pattern: /^\/campaigns\/[^/]+$/, permissions: ['marketing.campaign.view'] },
];

/**
 * The permissions a path needs — `[]` for a page everyone may open (dashboard, tasks), `undefined`
 * for a path the navigation does not know (account, notifications: the page decides).
 *
 * The same rule as the menu, applied to a typed or followed address: a page whose section is not
 * someone's work does not mount, so it never asks the server for data it would then have to hide.
 * The server refuses those requests regardless (ADR-0006).
 */
export function routePermissions(pathname: string): Permission[] | undefined {
  for (const route of RECORD_ROUTES) if (route.pattern.test(pathname)) return route.permissions;
  return activeItem(pathname)?.permissions;
}

export function visibleGroups(canAny: (permissions: readonly Permission[]) => boolean): NavGroup[] {
  return NAVIGATION.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.permissions.length === 0 || canAny(item.permissions)),
  })).filter((group) => group.items.length > 0);
}

/** The entry a path belongs to: the longest navigation path that is a prefix of it. */
export function activeItem(pathname: string): NavItem | undefined {
  let best: NavItem | undefined;
  for (const group of NAVIGATION) {
    for (const item of group.items) {
      const matches =
        item.path === '/'
          ? pathname === '/'
          : pathname === item.path || pathname.startsWith(`${item.path}/`);
      if (matches && (!best || item.path.length > best.path.length)) best = item;
    }
  }
  return best;
}

export function isActive(item: NavItem, pathname: string): boolean {
  return activeItem(pathname)?.path === item.path;
}

/**
 * The breadcrumb trail for a path.
 *
 * Derived from the navigation model rather than from the URL, so a crumb always matches a place a
 * person can actually go back to. A detail screen appends its own final crumb (a record number)
 * through `useBreadcrumbTail`, because only the page knows it.
 */
export function breadcrumbFor(pathname: string): NavItem[] {
  const trail: NavItem[] = [];
  const home = NAVIGATION[0]?.items[0];
  if (home) trail.push(home);
  if (pathname === '/') return trail;
  const item = activeItem(pathname);
  if (item && item.path !== '/') trail.push(item);
  return trail;
}
